import { Loader2 } from 'lucide-react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { selectGrantsStatus } from '@/stores/grants';
import { useBotAccess } from '@/hooks/useBotAccess';
import { canAccessBotOperatorSurface, canAccessBotSurface, type Role } from '@/types/navigation';

/** Direct bot URLs use the same resolved predicate as the navigation registry.
 * Revoking access rerenders this guard immediately and ejects an open bot page
 * before any bot control can remain mounted. Until this session confirms the
 * trader's grant the page is neither mounted nor redirected away: a deep link
 * opened right after login must not be lost to a not-yet-loaded grant. */
export function BotAccessRoute() {
  const role = (useAuthStore((s) => s.user?.role) ?? 'trader') as Role;
  const botAccess = useBotAccess();
  const grants = useAuthStore(selectGrantsStatus);
  if (canAccessBotSurface(role, botAccess)) return <Outlet />;
  if (grants === 'ready') return <Navigate to="/dashboard" replace />;
  return (
    <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm" role="status">
      {grants === 'error' ? (
        <>
          Couldn't confirm bot access.
          <button
            type="button"
            onClick={() => { void useAuthStore.getState().refreshMe(); }}
            className="underline underline-offset-2"
          >
            Retry
          </button>
        </>
      ) : <><Loader2 className="h-4 w-4 animate-spin" />Confirming bot access…</>}
    </div>
  );
}

export function BotsIndexRoute() {
  const role = (useAuthStore((s) => s.user?.role) ?? 'trader') as Role;
  const target = canAccessBotOperatorSurface(role) ? '/bots/vanilla' : '/bots/g9';
  return <Navigate to={target} replace />;
}

export function BotOperatorRoute() {
  const role = (useAuthStore((s) => s.user?.role) ?? 'trader') as Role;
  return canAccessBotOperatorSurface(role) ? <Outlet /> : <Navigate to="/bots/g9" replace />;
}
